import React, { FunctionComponent, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SelectField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanModalityToggle } from './NpTestPlanModalityToggle'
import { NpTestPlanCondition, NpTestPlanGradeLevel, NpTestPlanModality } from '../data/npTestPlanTypes'
import { NP_TEST_PLAN_NUMBERED_GRADES } from '../npTestPlanConstants'

export interface NpTestPlanBuildPanelProps {
  conditions: NpTestPlanCondition[]
  isCreatingPlan: boolean
  /** Preselected, and the baseline the warning compares against. */
  scheduledModality: NpTestPlanModality
  memberFirstName: string
  onStartBuild: (selection: {
    modality: NpTestPlanModality
    conditionId: string
    gradeLevel?: NpTestPlanGradeLevel
  }) => void
}

const Card = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  borderWidth: 1,
  borderColor: colors.borderDefault,
  borderStyle: 'solid',
  borderRadius: '16px',
  backgroundColor: colors.backgroundSection,
  paddingVertical: spacing['48px'],
  paddingHorizontal: spacing['24px'],
  alignItems: 'center',
}))

const Form = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '480px',
  maxWidth: '100%',
  gap: spacing['24px'],
}))

const Field = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
}))

/**
 * `BaseInput` reserves 40px under every field for an error message this form never sets, which
 * would leave the selects sitting nearly three times further apart than the rest of the fields.
 */
const NO_RESERVED_ERROR_SPACE = { marginBottom: 0 }

export const NpTestPlanBuildPanel: FunctionComponent<NpTestPlanBuildPanelProps> = ({
  conditions,
  isCreatingPlan,
  scheduledModality,
  memberFirstName,
  onStartBuild,
}) => {
  const { formatMessage } = useIntl()
  const [modality, setModality] = useState<NpTestPlanModality>(scheduledModality)
  const [conditionId, setConditionId] = useState<string>('')
  const [gradeLevel, setGradeLevel] = useState<string>('')
  const hasModalityChanged = modality !== scheduledModality

  /**
   * Only batteries with grade-dependent measures ask for a grade, and they ask
   * every member regardless of age. Answering is optional: a grade narrows which measures are
   * appropriate, so its absence costs precision rather than making the build invalid.
   */
  const usesGradeLevel = Boolean(conditions.find(({ id }) => id === conditionId)?.usesGradeLevel)

  const gradeLevelOptions = useMemo(
    () => [
      {
        value: NpTestPlanGradeLevel.PRE_K,
        label: formatMessage({
          defaultMessage: 'Pre-K',
          description: 'Grade level option for a member who has not started kindergarten',
        }),
      },
      {
        value: NpTestPlanGradeLevel.KINDERGARTEN,
        label: formatMessage({
          defaultMessage: 'Kindergarten',
          description: 'Grade level option for a member in kindergarten',
        }),
      },
      ...NP_TEST_PLAN_NUMBERED_GRADES.map(({ grade, value }) => ({
        value,
        label: formatMessage(
          {
            defaultMessage:
              '{grade, select, 1 {1st grade} 2 {2nd grade} 3 {3rd grade} 4 {4th grade} 5 {5th grade} 6 {6th grade} 7 {7th grade} 8 {8th grade} 9 {9th grade} 10 {10th grade} 11 {11th grade} 12 {12th grade} other {Grade {grade}}}',
            description: 'Grade level option for a member in one of the numbered school grades',
          },
          { grade: String(grade) },
        ),
      })),
      {
        value: NpTestPlanGradeLevel.POST_SECONDARY,
        label: formatMessage({
          defaultMessage: 'Post-secondary',
          description: 'Grade level option for a member who has finished school',
        }),
      },
    ],
    [formatMessage],
  )

  return (
    <Card testID={tID('NpTestPlanBuildPanel')}>
      <Form>
        <BodyText
          size={BodyTextSize.DEFAULT}
          text={formatMessage({
            defaultMessage:
              'Start building from a standard battery by selecting a modality and conditions to evaluate.',
            description: 'Explains how to start building a neuropsych test plan',
          })}
        />
        <Field>
          <BodyText
            size={BodyTextSize.SMALL}
            text={formatMessage({
              defaultMessage: 'Modality',
              description: 'Label for the modality the neuropsych testing battery is delivered in',
            })}
          />
          <NpTestPlanModalityToggle value={modality} onChange={setModality} disabled={isCreatingPlan} />
        </Field>
        {hasModalityChanged && (
          <InlineWarningBanner
            testId={tID('NpTestPlanBuildPanel-modalityChangedWarning')}
            marginBottom='0px'
            padding='16px'
            title={formatMessage(
              {
                defaultMessage:
                  '{memberFirstName} is scheduled for a {scheduledModality, select, inPerson {in person} other {virtual}} evaluation',
                description:
                  'Warns that the neuropsych evaluation is already scheduled for a different modality than the one just picked',
              },
              { memberFirstName, scheduledModality },
            )}
            text={formatMessage({
              defaultMessage: 'Changing the modality of the evaluation may require scheduling new testing sessions.',
              description: 'Explains the consequence of changing the modality of a scheduled neuropsych evaluation',
            })}
          />
        )}
        <SelectField
          useStringValue
          baseInputStyle={NO_RESERVED_ERROR_SPACE}
          label={formatMessage({
            defaultMessage: 'Condition',
            description: 'Label for the condition a neuropsych testing battery evaluates',
          })}
          name='npTestPlanCondition'
          value={conditionId}
          onChange={(value) => {
            setConditionId(String(value))
            // The grades on offer belong to the battery, so a grade picked for the previous
            // condition must not survive into a build it was never chosen for.
            setGradeLevel('')
          }}
          disabled={isCreatingPlan}
          options={conditions.map(({ id, name }) => ({ label: name, value: id }))}
        />
        {usesGradeLevel && (
          <SelectField
            useStringValue
            baseInputStyle={NO_RESERVED_ERROR_SPACE}
            label={formatMessage({
              defaultMessage: 'Grade level (optional)',
              description: 'Label for the school grade a neuropsych testing battery is normed against',
            })}
            name='npTestPlanGradeLevel'
            value={gradeLevel}
            onChange={(value) => setGradeLevel(String(value))}
            disabled={isCreatingPlan}
            options={gradeLevelOptions}
          />
        )}
        <PrimaryButton
          testID={tID('NpTestPlanBuildPanel-startBuild')}
          fullWidth
          loading={isCreatingPlan}
          disabled={!conditionId}
          onPress={() =>
            onStartBuild({
              modality,
              conditionId,
              gradeLevel: usesGradeLevel && gradeLevel ? (gradeLevel as NpTestPlanGradeLevel) : undefined,
            })
          }
          text={formatMessage({
            defaultMessage: 'Start build',
            description: 'Button that creates a neuropsych test plan from the selected standard battery',
          })}
        />
      </Form>
    </Card>
  )
}
